import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { decodeAal, decodeBednbodyClaims, type BednbodyClaims } from "./jwt-claims";
import { queryClient } from "./query-client";

export type SignInResult =
  | { status: "signed_in" }
  | { status: "mfa_required"; factorId: string }
  /** First staff login: no verified TOTP factor yet → enroll (QR + secret). */
  | { status: "mfa_setup"; factorId: string; qrCode: string; secret: string }
  | { status: "error"; message: string };

interface AuthState {
  session: Session | null;
  claims: BednbodyClaims | null;
  loading: boolean;
  /** AAL2 = MFA challenge satisfied. All admin roles require AAL2 to be considered authenticated. */
  aal: "aal1" | "aal2" | null;
  signInWithPassword: (email: string, password: string) => Promise<SignInResult>;
  verifyMfa: (factorId: string, code: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase!.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase!.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  // Both derived synchronously from the token so they can never lag behind
  // `session` (an async aal lookup here races the post-MFA redirect).
  const { claims, aal } = useMemo(() => {
    if (!session?.access_token) return { claims: null, aal: null };
    return {
      claims: decodeBednbodyClaims(session.access_token),
      aal: decodeAal(session.access_token),
    };
  }, [session?.access_token]);

  const value: AuthState = {
    session,
    claims,
    loading,
    aal,
    async signInWithPassword(email, password) {
      const { error } = await supabase!.auth.signInWithPassword({ email, password });
      if (error) return { status: "error", message: error.message };

      // Password success only gets you AAL1. Every staff role requires AAL2
      // (TOTP) per vault ARCHITECTURE.md §Auth flow — and the access-token
      // hook (0021) withholds the PostgREST role below aal2, so there is no
      // "skip MFA" shortcut that produces a working session. First login on
      // a fresh account therefore ENROLLS a factor instead of bypassing.
      const { data: aalData } = await supabase!.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData?.currentLevel === "aal2") return { status: "signed_in" };

      const { data: factorsData } = await supabase!.auth.mfa.listFactors();
      const verified = factorsData?.totp?.find((f) => f.status === "verified");
      if (verified) return { status: "mfa_required", factorId: verified.id };

      // No verified factor: clear stale half-finished enrollments (their QR
      // can't be re-shown), then start a fresh one.
      for (const stale of factorsData?.totp ?? []) {
        await supabase!.auth.mfa.unenroll({ factorId: stale.id });
      }
      const { data: enrollment, error: enrollError } = await supabase!.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Bednbody Admin",
      });
      if (enrollError || !enrollment) {
        return { status: "error", message: enrollError?.message ?? "Could not start MFA setup." };
      }
      return {
        status: "mfa_setup",
        factorId: enrollment.id,
        qrCode: enrollment.totp.qr_code,
        secret: enrollment.totp.secret,
      };
    },
    async verifyMfa(factorId, code) {
      const { data: challenge, error: challengeError } = await supabase!.auth.mfa.challenge({
        factorId,
      });
      if (challengeError || !challenge) {
        return { error: challengeError?.message ?? "Could not start MFA challenge." };
      }
      const { error } = await supabase!.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (!error) {
        const { data: refreshed } = await supabase!.auth.getSession();
        setSession(refreshed.session);
      }
      return { error: error?.message ?? null };
    },
    async signOut() {
      await supabase!.auth.signOut();
      // Otherwise the next person to sign in on this device sees the
      // previous account's cached rosters/dashboards until something
      // unrelated triggers a refetch.
      queryClient.clear();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
